import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';

const ATTENDANCE_STORAGE_KEY = 'ace_attendance_records_v1';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';

const TabletGlobalStyle = createGlobalStyle`
  body {
    background: #020617;
  }
`;

const Wrapper = styled.main`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.2rem;
  background: radial-gradient(circle at top, #0f172a 0%, #020617 65%);
  color: #e2e8f0;
`;

const Card = styled.section`
  width: min(1200px, 100%);
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 24px;
  padding: 1.5rem;
  box-shadow: 0 18px 42px rgba(2, 6, 23, 0.45);
`;

const Header = styled.header`
  margin-bottom: 1.5rem;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #93c5fd;
  font-size: 0.82rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
`;

const Title = styled.h1`
  margin: 0.45rem 0 0.5rem;
  font-size: clamp(1.5rem, 2.8vw, 2rem);
`;

const Description = styled.p`
  margin: 0;
  color: #94a3b8;
  line-height: 1.6;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1rem;
  margin-top: 1.4rem;
`;

const SearchInput = styled.input`
  width: 100%;
  border: 1px solid rgba(148, 163, 184, 0.28);
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 14px;
  padding: 0.95rem 1rem;
  font-size: 1rem;
`;

const SectionLabel = styled.p`
  margin: 0;
  color: #93c5fd;
  font-size: 0.84rem;
  font-weight: 700;
`;

const RecommendInline = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const RecommendRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding-bottom: 0.1rem;
`;

const RecommendStudentButton = styled.button`
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: #172036;
  color: #e2e8f0;
  border-radius: 999px;
  padding: 0.4rem 0.75rem;
  font-size: 0.82rem;
  cursor: pointer;
  white-space: nowrap;
`;

const StudentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.5rem;
  max-height: 360px;
  overflow: auto;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 800px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const StudentCard = styled.button`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  width: 100%;
  border: 1px solid ${({ $active }) => ($active ? '#60a5fa' : 'rgba(148, 163, 184, 0.3)')};
  background: ${({ $active }) => ($active ? '#1d2d50' : '#111b31')};
  color: #e2e8f0;
  border-radius: 10px;
  padding: 0.52rem 0.6rem;
  cursor: pointer;
  text-align: left;
`;

const Avatar = styled.span`
  width: 30px;
  height: 30px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  background: ${({ $seed }) => {
    const colors = ['#3b82f6', '#06b6d4', '#8b5cf6', '#f59e0b', '#10b981'];
    return colors[$seed % colors.length];
  }};
  color: #fff;
`;

const StudentMeta = styled.div`
  display: grid;
  gap: 0.1rem;
`;

const StudentName = styled.span`
  font-weight: 700;
  font-size: 0.9rem;
`;

const StudentSub = styled.span`
  color: #94a3b8;
  font-size: 0.75rem;
`;

const PinRow = styled.div`
  display: flex;
  gap: 0.6rem;
`;

const PinBox = styled.input`
  width: 56px;
  height: 56px;
  text-align: center;
  font-size: 1.35rem;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: #0b1224;
  color: #e2e8f0;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  border: 0;
  border-radius: 12px;
  padding: 0.85rem 1.2rem;
  font-weight: 700;
  cursor: pointer;
  transition: 0.2s ease;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  color: #fff;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const GhostButton = styled(Button)`
  background: #1e293b;
  color: #cbd5e1;
`;

const Message = styled.p`
  margin: 0.2rem 0 0;
  color: ${({ $error }) => ($error ? '#fda4af' : '#86efac')};
  font-weight: 600;
`;

const Hint = styled.p`
  margin: 0;
  color: #94a3b8;
  font-size: 0.9rem;
`;

const formatTimestamp = (iso) =>
  new Date(iso).toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

const getDisplayName = (student) => {
  const level = (student.school_level || '').toLowerCase();
  const isElementary = level.includes('초');
  if (isElementary) {
    return student.english_nickname || student.student_name_en || student.student_name_ko || '이름없음';
  }
  return student.student_name_ko || student.student_name_en || student.english_nickname || '이름없음';
};

const getKoreanAge = (birthDate) => {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  return now.getFullYear() - birth.getFullYear() + 1;
};

const recommendedNamesByWeekday = {
  1: ['Leo', 'Ken', 'Seho'],
  2: ['Remy', 'Ho', 'Kai', 'Kei', 'Ken', 'Ethan'],
  4: ['Eden', 'Leo', 'Alex', 'Ken', 'Kei', 'Jack', 'Key', 'Logan', 'Ethan'],
  5: ['Ho', 'Alex', 'Ken', 'Kai', 'Seho'],
  6: ['Logan', 'Remy', '이윤정', '김나림'],
};

const saveAttendanceRecord = (record) => {
  const stored = localStorage.getItem(ATTENDANCE_STORAGE_KEY);
  const parsed = stored ? JSON.parse(stored) : [];
  const nextRecord = {
    id: record.id || `${Date.now()}-${record.studentId}`,
    studentId: record.studentId,
    studentName: record.studentName,
    studentEnglishName: record.studentEnglishName || '',
    classroom: record.classroom,
    status: record.status || '입실',
    checkedAt: record.checkedAt || new Date().toISOString(),
    source: record.source || 'kiosk',
  };
  const next = [nextRecord, ...parsed].slice(0, 500);
  localStorage.setItem(ATTENDANCE_STORAGE_KEY, JSON.stringify(next));
  return nextRecord;
};

const AttendanceKioskPage = () => {
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [searchText, setSearchText] = useState('');
  const [pinDigits, setPinDigits] = useState(['', '', '', '']);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', error: false });
  const pinInputRefs = useRef([]);

  const selectedStudent = useMemo(
    () => students.find((student) => String(student.id) === selectedId),
    [selectedId, students]
  );
  const filteredStudents = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();
    const matched = students.filter((student) => {
      if (!keyword) return true;
      const name = getDisplayName(student).toLowerCase();
      const altName = (student.student_name_ko || student.student_name_en || '').toLowerCase();
      const nickname = (student.english_nickname || '').toLowerCase();
      const classroom = (student.classroom_label || '').toLowerCase();
      return (
        name.includes(keyword) ||
        altName.includes(keyword) ||
        nickname.includes(keyword) ||
        classroom.includes(keyword)
      );
    });
    const sorted = [...matched].sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b), 'ko'));
    return sorted;
  }, [students, searchText]);
  const expectedStudents = useMemo(() => {
    const todayWeekday = new Date().getDay();
    const recommendedNames = recommendedNamesByWeekday[todayWeekday] || [];
    const normalizedNames = recommendedNames.map((name) => name.toLowerCase());
    const matched = students.filter((student) => {
      const displayName = getDisplayName(student).toLowerCase();
      const koreanName = (student.student_name_ko || '').toLowerCase();
      const englishNickname = (student.english_nickname || '').toLowerCase();
      const englishName = (student.student_name_en || '').toLowerCase();
      return normalizedNames.some(
        (name) =>
          name === displayName ||
          name === koreanName ||
          name === englishNickname ||
          name === englishName
      );
    });
    return matched.sort((a, b) => {
      const aIndex = normalizedNames.findIndex((name) =>
        [getDisplayName(a), a.student_name_ko, a.english_nickname, a.student_name_en]
          .filter(Boolean)
          .map((text) => String(text).toLowerCase())
          .includes(name)
      );
      const bIndex = normalizedNames.findIndex((name) =>
        [getDisplayName(b), b.student_name_ko, b.english_nickname, b.student_name_en]
          .filter(Boolean)
          .map((text) => String(text).toLowerCase())
          .includes(name)
      );
      return aIndex - bIndex;
    });
  }, [students]);
  const pin = pinDigits.join('');

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoadingStudents(true);
        const response = await fetch(`${backendUrl}/api/attendance/students`);
        if (!response.ok) {
          throw new Error('학생 목록 요청 실패');
        }
        const data = await response.json();
        setStudents(Array.isArray(data.students) ? data.students : []);
      } catch (error) {
        setMessage({ text: '학생 목록을 불러오지 못했습니다. 관리자에게 문의해주세요.', error: true });
      } finally {
        setLoadingStudents(false);
      }
    };

    fetchStudents();
  }, []);

  const handlePinChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    setPinDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    if (digit && index < 3) {
      pinInputRefs.current[index + 1]?.focus();
    }
  };

  const handlePinKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !pinDigits[index] && index > 0) {
      pinInputRefs.current[index - 1]?.focus();
    }
  };

  const resetForm = () => {
    setSelectedId('');
    setPinDigits(['', '', '', '']);
  };

  const handleSubmit = async () => {
    if (!selectedStudent) {
      setMessage({ text: '학생을 먼저 선택해주세요.', error: true });
      return;
    }
    if (pin.length !== 4) {
      setMessage({ text: 'PIN 4자리를 입력해주세요.', error: true });
      return;
    }
    try {
      setSubmitting(true);
      const response = await fetch(`${backendUrl}/api/attendance/checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id: selectedStudent.id,
          pin,
        }),
      });
      const payload = await response.json();
      if (!response.ok || payload.status !== 'ok') {
        throw new Error(payload.message || 'PIN 검증 실패');
      }

      const record = saveAttendanceRecord({
        ...payload.record,
        studentEnglishName:
          payload.record.studentEnglishName ||
          selectedStudent.english_nickname ||
          selectedStudent.student_name_en ||
          '',
      });
      setMessage({
        text: `${record.studentName} 학생 입실 완료 (${formatTimestamp(record.checkedAt)})`,
        error: false,
      });
      setPinDigits(['', '', '', '']);
      pinInputRefs.current[0]?.focus();
    } catch (error) {
      setMessage({ text: error.message || '입실 처리에 실패했습니다.', error: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <TabletGlobalStyle />
      <Wrapper>
        <Card>
          <Header>
            <Eyebrow>Tablet Attendance</Eyebrow>
            <Title>출결 체크</Title>
            <Description>
              학생을 검색하고 카드로 선택한 뒤 4자리 PIN을 입력해주세요.
            </Description>
          </Header>

          <FormGrid>
            <SearchInput
              type="text"
              placeholder="이름/닉네임/반 검색"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              disabled={loadingStudents || submitting}
            />

            <RecommendInline>
              <SectionLabel>추천 |</SectionLabel>
              <RecommendRow>
                {expectedStudents.map((student) => (
                  <RecommendStudentButton
                    key={`expected-${student.id}`}
                    type="button"
                    onClick={() => setSelectedId(String(student.id))}
                    disabled={submitting}
                  >
                    {getDisplayName(student)}
                  </RecommendStudentButton>
                ))}
                {expectedStudents.length === 0 ? <Hint>오늘 추천 학생이 없습니다.</Hint> : null}
              </RecommendRow>
            </RecommendInline>

            <SectionLabel>학생 선택</SectionLabel>
            <StudentGrid>
              {filteredStudents.map((student) => {
                const displayName = getDisplayName(student);
                const age = getKoreanAge(student.birth_date);
                return (
                  <StudentCard
                    key={student.id}
                    type="button"
                    onClick={() => setSelectedId(String(student.id))}
                    disabled={submitting}
                    $active={selectedId === String(student.id)}
                  >
                    <Avatar $seed={student.id}>{displayName?.slice(0, 1) || '?'}</Avatar>
                    <StudentMeta>
                      <StudentName>{displayName}</StudentName>
                      <StudentSub>
                        {student.classroom_label}
                        {age ? ` · ${age}세` : ''}
                      </StudentSub>
                    </StudentMeta>
                  </StudentCard>
                );
              })}
            </StudentGrid>
            {filteredStudents.length === 0 ? <Hint>검색 결과가 없습니다.</Hint> : null}
            {selectedStudent ? (
              <Hint>
                선택됨: {getDisplayName(selectedStudent)} ({selectedStudent.classroom_label})
              </Hint>
            ) : (
              <Hint>학생 카드를 먼저 선택해주세요.</Hint>
            )}

            <PinRow>
              {pinDigits.map((digit, index) => (
                <PinBox
                  key={`pin-${index}`}
                  ref={(el) => {
                    pinInputRefs.current[index] = el;
                  }}
                  value={digit}
                  inputMode="numeric"
                  maxLength={1}
                  type="password"
                  onChange={(event) => handlePinChange(index, event.target.value)}
                  onKeyDown={(event) => handlePinKeyDown(index, event)}
                  disabled={submitting}
                  aria-label={`PIN ${index + 1}번째 자리`}
                />
              ))}
            </PinRow>

            <ActionRow>
              <Button type="button" onClick={handleSubmit} disabled={loadingStudents || submitting}>
                입실 체크
              </Button>
              <GhostButton type="button" onClick={resetForm} disabled={submitting}>
                초기화
              </GhostButton>
            </ActionRow>

            {message.text ? <Message $error={message.error}>{message.text}</Message> : null}
            {loadingStudents ? <Hint>학생 목록을 불러오는 중입니다...</Hint> : null}
          </FormGrid>
        </Card>
      </Wrapper>
    </>
  );
};

export { ATTENDANCE_STORAGE_KEY };
export default AttendanceKioskPage;
